import { useState, useEffect } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
  Users, IndianRupee, CreditCard, TrendingUp, UserPlus, Route, Repeat,
  Wallet, BadgeCheck, Search, Video, Layers, Wrench, Globe, PieChart as PieIcon,
  ArrowUpRight, Calendar, Loader2, Zap, CheckCircle2, Undo2, MessageSquareQuote,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtDay = (d) => {
  const [, m, day] = (d || "").split("-");
  const M = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return m ? `${M[+m]} ${+day}` : d;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const PIE_COLORS = ["#0055FF", "#10b981", "#ec4899", "#f59e0b", "#06b6d4", "#8b5cf6"];

function KCard({ icon: Icon, label, value, sub, accent = "indigo", testid }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
    pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
    orange: "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <span className={`grid place-items-center h-9 w-9 rounded-xl ${tones[accent]}`}><Icon className="h-5 w-5" /></span>
      <p className="mt-3 font-heading font-extrabold text-3xl tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground/80 mt-1">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-heading font-semibold text-base">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Bars({ rows, labelKey, valueKey, color = "#0055FF" }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={(r[labelKey] || "") + i} className="flex items-center gap-3 text-sm">
          <span className="w-40 shrink-0 truncate text-muted-foreground">{r[labelKey]}</span>
          <div className="flex-1 h-3 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(r[valueKey] / max) * 100}%`, background: color }} />
          </div>
          <span className="w-10 text-right font-semibold">{r[valueKey]}</span>
        </div>
      ))}
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-2">No data yet.</p>}
    </div>
  );
}

/* ---------------------------------- BUSINESS --------------------------------- */
export function BusinessTab({ data }) {
  if (!data) return null;
  const { users, subscribers, revenue, conversion_rate } = data;
  const planPie = [
    { name: "Monthly", value: subscribers.monthly },
    { name: "Yearly", value: subscribers.yearly },
  ].filter((p) => p.value > 0);

  return (
    <div className="space-y-6" data-testid="business-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KCard testid="kpi-learners" icon={Users} label="Total learners" value={users.total.toLocaleString("en-IN")} sub={`+${users.new_today} today · +${users.new_7d} this week`} />
        <KCard testid="kpi-subscribers" icon={BadgeCheck} label="Active subscribers" value={subscribers.active.toLocaleString("en-IN")} sub={`${subscribers.cancelled} cancelled`} accent="emerald" />
        <KCard testid="kpi-mrr" icon={IndianRupee} label="MRR (recurring/mo)" value={inr(revenue.mrr)} sub={`ARR ${inr(revenue.arr)}`} accent="pink" />
        <KCard testid="kpi-conversion" icon={TrendingUp} label="Signup → paid" value={`${conversion_rate}%`} sub={`${subscribers.active} of ${users.total}`} accent="orange" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KCard testid="kpi-new-30d" icon={UserPlus} label="New signups (30d)" value={users.new_30d.toLocaleString("en-IN")} />
        <KCard testid="kpi-paths" icon={Route} label="Roadmaps built" value={users.paths_built.toLocaleString("en-IN")} accent="emerald" />
        <KCard testid="kpi-lifetime" icon={Wallet} label="Lifetime revenue" value={inr(revenue.lifetime)} sub={`${revenue.transactions} payments`} accent="pink" />
        <KCard testid="kpi-month-rev" icon={Calendar} label="Revenue this month" value={inr(revenue.this_month)} accent="orange" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard title="New signups — last 30 days">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.signups_daily} margin={{ left: -20, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0055FF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} interval={4} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip labelFormatter={fmtDay} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#0055FF" strokeWidth={2.5} fill="url(#sg)" name="Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Plan mix">
          {planPie.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={planPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {planPie.map((e, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] grid place-items-center text-center text-sm text-muted-foreground">
              <div><PieIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />No active subscriptions yet.<br />They'll appear here after launch.</div>
            </div>
          )}
          <div className="flex justify-center gap-4 text-xs mt-2">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[0] }} /> Monthly {subscribers.monthly}</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[1] }} /> Yearly {subscribers.yearly}</span>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

/* ---------------------------------- REVENUE ---------------------------------- */
export function RevenueTab({ overview, payments }) {
  if (!overview) return null;
  const { revenue, subscribers } = overview;
  const list = (payments && payments.payments) || [];
  return (
    <div className="space-y-6" data-testid="revenue-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KCard testid="rev-mrr" icon={Repeat} label="MRR" value={inr(revenue.mrr)} sub="Monthly recurring" />
        <KCard testid="rev-arr" icon={TrendingUp} label="ARR" value={inr(revenue.arr)} sub="Annual run-rate" accent="emerald" />
        <KCard testid="rev-month" icon={Calendar} label="This month" value={inr(revenue.this_month)} accent="pink" />
        <KCard testid="rev-lifetime" icon={Wallet} label="Lifetime" value={inr(revenue.lifetime)} sub={`${revenue.transactions} payments`} accent="orange" />
      </div>

      <ChartCard title="Revenue — last 30 days" right={<span className="text-xs text-muted-foreground">{inr(revenue.last_30d)} total</span>}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={overview.revenue_daily} margin={{ left: -8, right: 8, top: 4 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} interval={4} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} stroke="hsl(var(--muted-foreground))" />
            <Tooltip labelFormatter={fmtDay} formatter={(v) => [inr(v), "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
            <Area type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2.5} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
        {revenue.lifetime === 0 && (
          <p className="mt-3 text-xs text-muted-foreground text-center">No payments recorded yet — this chart fills automatically once real subscriptions start.</p>
        )}
      </ChartCard>

      <div className="rounded-2xl border border-border bg-card p-5" data-testid="churn-reasons-card">
        <h3 className="font-heading font-semibold text-base mb-1 flex items-center gap-2">
          <Undo2 className="h-4 w-4 text-destructive" /> Why users refund
        </h3>
        {(!payments?.refunds || payments.refunds.count === 0) ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="churn-empty">No refunds yet 🎉 — churn reasons will appear here when someone uses the 7-day guarantee.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-4">
              <b className="text-foreground">{payments.refunds.count}</b> refund{payments.refunds.count === 1 ? "" : "s"} ·{" "}
              <b className="text-destructive">{inr(payments.refunds.total_refunded)}</b> returned
            </p>
            <div className="space-y-2" data-testid="churn-reason-bars">
              {payments.refunds.reasons.map((r) => {
                const pct = Math.round((r.count / payments.refunds.count) * 100);
                return (
                  <div key={r.reason} className="flex items-center gap-3">
                    <span className="w-44 shrink-0 text-xs font-semibold truncate">{r.reason}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-destructive/70" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs text-muted-foreground">{r.count} · {pct}%</span>
                  </div>
                );
              })}
            </div>
            {payments.refunds.recent.some((r) => r.note) && (
              <div className="mt-4 space-y-2">
                {payments.refunds.recent.filter((r) => r.note).slice(0, 5).map((r, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-xl bg-muted/60 px-3 py-2">
                    <MessageSquareQuote className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground leading-relaxed">"{r.note}" <span className="text-[10px]">— {r.email}</span></p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold text-base mb-4 flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Recent transactions</h3>
        <div className="overflow-x-auto" data-testid="payments-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="pb-2 font-semibold">Date</th><th className="pb-2 font-semibold">Customer</th>
                <th className="pb-2 font-semibold">Plan</th><th className="pb-2 font-semibold">Source</th>
                <th className="pb-2 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.payment_id} className="border-b border-border/50">
                  <td className="py-2.5 text-muted-foreground">{fmtDate(p.created_at)}</td>
                  <td className="py-2.5"><span className="font-medium">{p.name || "—"}</span><span className="block text-xs text-muted-foreground">{p.email}</span></td>
                  <td className="py-2.5 capitalize">{p.plan}</td>
                  <td className="py-2.5 text-muted-foreground capitalize">
                    {p.status === "refunded"
                      ? <span className="text-xs font-bold text-destructive normal-case">Refunded{p.refund_reason ? ` — ${p.refund_reason}` : ""}</span>
                      : p.source}
                  </td>
                  <td className={`py-2.5 text-right font-semibold ${p.status === "refunded" ? "text-destructive line-through" : ""}`}>{inr(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No transactions yet. Real payments will show here after launch.</p>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Note: MRR/ARR are computed live from {subscribers.active} active subscription(s). Revenue totals come from captured payments.</p>
    </div>
  );
}

/* -------------------------------- SUBSCRIBERS -------------------------------- */
export function SubscribersTab({ data }) {
  const [q, setQ] = useState("");
  if (!data) return null;
  const rows = data.subscribers.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  const monthly = data.subscribers.filter((r) => r.plan === "monthly").length;
  const yearly = data.subscribers.filter((r) => r.plan === "yearly").length;
  return (
    <div className="space-y-5" data-testid="subscribers-tab">
      <div className="grid grid-cols-3 gap-4">
        <KCard testid="sub-active" icon={BadgeCheck} label="Active subscribers" value={data.active} accent="emerald" />
        <KCard testid="sub-monthly" icon={Repeat} label="Monthly plan" value={monthly} />
        <KCard testid="sub-yearly" icon={Users} label="Yearly plan" value={yearly} />
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="subscriber-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 rounded-xl" />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="pb-2 font-semibold">Customer</th><th className="pb-2 font-semibold">Plan</th>
              <th className="pb-2 font-semibold">Status</th><th className="pb-2 font-semibold">Started</th>
              <th className="pb-2 font-semibold">Renews</th><th className="pb-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.subscription_id || r.email} className="border-b border-border/50">
                <td className="py-2.5"><span className="font-medium">{r.name}</span><span className="block text-xs text-muted-foreground">{r.email}</span></td>
                <td className="py-2.5 capitalize">{r.plan}</td>
                <td className="py-2.5">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>{r.status}</span>
                </td>
                <td className="py-2.5 text-muted-foreground">{fmtDate(r.activated_at)}</td>
                <td className="py-2.5 text-muted-foreground">{fmtDate(r.current_end)}</td>
                <td className="py-2.5 text-right font-semibold">{inr(r.amount)}/{r.plan === "yearly" ? "yr" : "mo"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No subscribers match.</p>}
      </div>
    </div>
  );
}

/* ---------------------------------- CONTENT ---------------------------------- */
export function ContentTab({ data }) {
  if (!data) return null;
  const langRows = Object.entries(data.videos.by_language).map(([language, count]) => ({ language, count }));
  return (
    <div className="space-y-6" data-testid="content-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KCard testid="content-active" icon={Video} label="Active lessons" value={data.videos.active.toLocaleString("en-IN")} sub={`${data.videos.dead} dead`} />
        <KCard testid="content-topics" icon={Layers} label="Topics covered" value={`${data.topics.covered}/${data.topics.total}`} sub={`${data.topics.uncovered_count} need videos`} accent="orange" />
        <KCard testid="content-tools" icon={Wrench} label="Tool courses built" value={data.tools.courses_built} sub={`${data.tools.total} tools in catalogue`} accent="emerald" />
        <KCard testid="content-langs" icon={Globe} label="Languages live" value={langRows.filter((r) => r.count > 0).length} accent="pink" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Active lessons by language">
          <Bars rows={langRows} labelKey="language" valueKey="count" />
        </ChartCard>
        <ChartCard title="Most-built tool courses">
          <Bars rows={data.tools.popular.map((t) => ({ name: t.name, learners: t.learners }))} labelKey="name" valueKey="learners" color="#10b981" />
        </ChartCard>
        <ChartCard title="Popular learner profiles">
          <Bars rows={data.archetypes} labelKey="label" valueKey="count" color="#ec4899" />
        </ChartCard>
        <ChartCard title="Top niches (deeper needs)">
          <Bars rows={data.niches} labelKey="label" valueKey="count" color="#f59e0b" />
        </ChartCard>
      </div>

      {data.topics.uncovered_count > 0 && (
        <div className="rounded-2xl border border-orange-300/40 bg-orange-50/50 dark:bg-orange-500/5 p-5">
          <h3 className="font-heading font-semibold text-base flex items-center gap-2 text-orange-700 dark:text-orange-400">
            <ArrowUpRight className="h-4 w-4" /> {data.topics.uncovered_count} topics still need lessons
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{data.topics.uncovered.join(" · ")}</p>
          <p className="mt-2 text-xs text-muted-foreground">Use the “API Quota → Curate entire platform” button to fill these within the daily YouTube budget.</p>
        </div>
      )}

      <ToolCoveragePanel />
    </div>
  );
}

export function ToolCoveragePanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tool-coverage");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load coverage.");
    }
  };
  useEffect(() => { load(); }, []);

  const curate = async (slug) => {
    setBusy(slug);
    try {
      const { data: r } = await api.post(`/admin/curate-tool/${slug}`);
      toast.success(r.message || "Queued.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not queue.");
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-6" data-testid="tool-coverage-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const s = data.summary;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="tool-coverage-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading font-semibold text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-primary" /> Tool course coverage (Content Health)
        </h3>
        <div className="flex gap-2 text-xs font-semibold">
          <span className="rounded-full bg-success/10 text-success px-2.5 py-1">{s.fully_curated} full</span>
          <span className="rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 px-2.5 py-1">{s.partial} partial</span>
          <span className="rounded-full bg-secondary text-muted-foreground px-2.5 py-1">{s.empty} empty</span>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Paid users who open a tool auto-jump the queue. Use “Curate now” to push any tool to the top manually.</p>
      <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {data.tools.map((t) => {
          const full = t.pct === 100;
          return (
            <div key={t.slug} className="flex items-center gap-3 rounded-xl border border-border/70 bg-background p-3" data-testid={`coverage-row-${t.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-sm truncate">{t.name}</span>
                  <span className="text-xs text-muted-foreground">{t.curated}/{t.total}</span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full rounded-full ${full ? "bg-success" : t.pct > 0 ? "bg-amber-500" : "bg-muted-foreground/40"}`} style={{ width: `${t.pct}%` }} />
                </div>
              </div>
              {full ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-success shrink-0"><CheckCircle2 className="h-4 w-4" /> 100%</span>
              ) : (
                <Button size="sm" variant="outline" className="rounded-lg shrink-0 h-8 text-xs"
                  data-testid={`curate-now-${t.slug}`} onClick={() => curate(t.slug)} disabled={busy === t.slug}>
                  {busy === t.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Zap className="h-3.5 w-3.5" /> Curate now</>}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
